// 訂閱方案卡 — /start（白底）與 /warroom（深底）共用同一份。
// 🔴 價目只能有一份：兩處各自寫死必然漂移。改價改這裡。
// 價目依 handbook 2026-07-26：基礎 NT$640（自己 1 + 對手 5）／進階 NT$1,600（20 家）。
// CTA → 藍新金流（QB_PAID_URL env·後端帶下來·換連結免 rebuild）。
// 本卡只出現在**登入後的產品介面**（對外素材不揭訂價）。

import { useState, useEffect } from 'react'
import { useLang } from '../i18n/LanguageContext'

// 🔴 數字**不在這裡**。價格與追蹤數的唯一正本是後端 checkup.PLANS，
//    經 GET /api/plans 下來。翻譯只翻文字，不碰數字。
//
//    為什麼改：這裡原本寫死 `pro: tracked [1, 20]`（＝自己 1 + 對手 20），
//    而後端當時只給 5 個對手 —— **對外已經在說一個系統做不到的數字**，
//    而且沒有任何機制會讓這兩邊對不上時發出聲音。
//
// FALLBACK 只在 /api/plans 拿不到時用，且**畫面會標示為暫定**（見下方 stale）。
// ⛔ 不要把它當成第二份正本來維護。
const FALLBACK = [
  { id: 'basic', price: 'NT$640', tracked: 6, highlight: true },
  { id: 'pro', price: 'NT$1,600', tracked: 20, highlight: false },
]

export function usePlans() {
  const [plans, setPlans] = useState(null)
  useEffect(() => {
    let alive = true
    fetch('/api/plans')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(d => {
        const paid = (d.plans || []).filter(x => x.id !== 'free')
        if (alive && paid.length) {
          setPlans(paid.map(x => ({ ...x, highlight: x.id === 'basic' })))
        } else if (alive) {
          setPlans(FALLBACK.map(x => ({ ...x, stale: true })))
        }
      })
      .catch(() => { if (alive) setPlans(FALLBACK.map(x => ({ ...x, stale: true }))) })
    return () => { alive = false }
  }, [])
  return plans
}

// 🔴 品牌名一律 **QuanBrand**（駝峰），三語都一樣 —— 不是 QUANBRAND、不是 Quanbrand。
// 全大寫是排版效果，不是品牌名；要視覺上的強調用 CSS，不要改字。
const S = {
  zh: {
    banner: 'QuanBrand 品牌戰情室',
    lead: ['免費版看得到「你自己有哪些缺口」。升級後，你看得到', '三個場域的完整動態',
           '——誰在追上來、你哪一項被超車、這個月該先補哪一塊。'],
    unit: '／月',
    plan: { basic: '基礎', pro: '進階' },
    tracked: (total) => `共 ${total} 個品牌（含自己）`,
    stale: '數字暫時無法確認，以結帳頁為準',
    features: [
      ['📊', '完整且即時的品牌報告', '不只分數——逐項證據、損失估算、優先順序，資料隨時更新'],
      ['🎯', '持續追蹤競爭對手', '每期自動比分，對手動作與排名變化一目了然'],
      ['🤖', '專屬品牌顧問機器人', '記得你的歷史與對手動態，跨期給建議，不用每次重講'],
      ['📈', '三個戰場一起看', 'Google 搜尋、Google 地圖、AI 推薦——被追過的那一刻不會漏掉'],
    ],
    cta: '立即升級 · 開始追蹤對手',
    foot: '安全付款由藍新金流處理',
    sub: {
      badge: '訂閱中',
      plan: { basic: '基礎方案', pro: '進階方案' },
      tracked: (used, limit) => `追蹤 ${used}/${limit} 個品牌（含自己）`,
      warroom: '進入品牌戰情室',
      manage: '額度與對手組合可隨時調整',
    },
  },
  en: {
    banner: 'QuanBrand War Room',
    lead: ['The free tier shows you your own gaps. Upgrading shows you ', 'all three places',
           ' — who is catching up, where you were overtaken, and what to fix first this month.'],
    unit: '/mo',
    plan: { basic: 'Basic', pro: 'Pro' },
    tracked: (total) => `${total} brands tracked (including your own)`,
    stale: 'Figures unconfirmed — checkout page is authoritative',
    features: [
      ['📊', 'A complete, live brand report', 'Not just a score — per-item evidence, cost estimates and priorities, kept up to date'],
      ['🎯', 'Continuous competitor tracking', 'Automatic scoring each period; their moves and rank changes at a glance'],
      ['🤖', 'Your own brand advisor bot', 'Remembers your history and your rivals, so you never have to explain it twice'],
      ['📈', 'All three battlegrounds at once', 'Google Search, Google Maps and AI recommendations — you will not miss the moment you are overtaken'],
    ],
    cta: 'Upgrade now · start tracking competitors',
    foot: 'Payments handled securely by NewebPay',
    sub: {
      badge: 'Subscribed',
      plan: { basic: 'Basic plan', pro: 'Pro plan' },
      tracked: (used, limit) => `Tracking ${used}/${limit} brands (including your own)`,
      warroom: 'Open the Brand War Room',
      manage: 'Quota and competitor set can be adjusted anytime',
    },
  },
  ja: {
    banner: 'QuanBrand ウォールーム',
    lead: ['無料版でわかるのは「自社の欠落」までです。アップグレードすると', '三つの場すべて',
           'が見えます——誰が迫っているか、どの項目で抜かれたか、今月どこから直すべきか。'],
    unit: '／月',
    plan: { basic: 'ベーシック', pro: 'プロ' },
    tracked: (total) => `合計 ${total} ブランド（自社を含む）`,
    stale: '数値を確認できません。決済ページが正となります',
    features: [
      ['📊', '完全かつ最新のブランドレポート', 'スコアだけでなく——項目ごとの根拠、損失試算、優先順位を常に最新の状態で'],
      ['🎯', '競合の継続トラッキング', '毎期自動で採点。競合の動きと順位変動が一目でわかります'],
      ['🤖', '専属ブランドアドバイザー', '御社の履歴と競合の動きを記憶。毎回説明し直す必要はありません'],
      ['📈', '3 つの戦場を同時に', 'Google 検索・Google マップ・AI レコメンド——抜かれた瞬間を見逃しません'],
    ],
    cta: '今すぐアップグレード · 競合の追跡を開始',
    foot: '決済は藍新金流が安全に処理します',
    sub: {
      badge: '購読中',
      plan: { basic: 'ベーシック', pro: 'プロ' },
      tracked: (used, limit) => `${used}/${limit} ブランドを追跡中（自社を含む）`,
      warroom: 'ブランドウォールームへ',
      manage: '枠と競合の組み合わせはいつでも調整できます',
    },
  },
}

// 付費用戶的狀態卡。🔴 **付費者不該再看到「立即升級」** ——
// 付錢之後畫面跟沒付錢一模一樣，等於付錢沒有效果（Allen 2026-08-24 實測）。
// 用法：tier === 'paid' 的地方以這張卡取代 PlanCard。
export function SubscriberCard({ plan = 'basic', tracking, theme = 'light', compact = false }) {
  const { lang } = useLang()
  const t = (S[lang] || S.zh).sub
  const dark = theme === 'dark'
  const box = dark
    ? { background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.35)' }
    : { background: '#EFF6FF', border: '1px solid #BFDBFE' }
  const title = dark ? '#DBEAFE' : '#1D4ED8'
  const dim = dark ? 'rgba(219,234,254,0.7)' : '#3B82F6'
  return (
    <div className="rounded-2xl px-4 py-3 text-left" style={box}>
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: dark ? 'rgba(59,130,246,0.25)' : '#DBEAFE', color: title }}>
          ✓ {t.badge}
        </span>
        <span className="text-sm font-semibold" style={{ color: title }}>
          {t.plan[plan] || plan}
        </span>
      </div>
      {tracking && tracking.limit != null && (
        <div className="text-xs mt-1.5" style={{ color: dim }}>
          {/* A-7：分母印方案本數（與方案卡同一個 20），加贈另外標 (+1)。
              limit（真上限 21）仍由後端算、仍是擋牆用的那個數 —— 這裡只是把
              「20 ＋ 1」拆開印，不是把 21 改成 20。 */}
          {t.tracked(tracking.used ?? 0, tracking.base ?? tracking.limit)}
          {tracking.bonus ? ` (+${tracking.bonus})` : ''}
        </div>
      )}
      {!compact && (
        <a href="/warroom"
          className="inline-block mt-2.5 px-3 py-1.5 rounded-lg text-white text-xs font-bold"
          style={{ background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)' }}>
          {t.warroom} →
        </a>
      )}
      <div className="text-[10px] mt-1.5" style={{ color: dim, opacity: 0.8 }}>{t.manage}</div>
    </div>
  )
}


export default function PlanCard({ paidUrl, compact = false, theme = 'light' }) {
  const plans = usePlans()
  // /warroom 沒有 LanguageProvider → useLang() 回 context 預設值 lang:'zh'，
  // 那一側維持中文、行為不變。**這是刻意的，不是漏掉。**
  const { lang } = useLang()
  const t = S[lang] || S.zh
  const dark = theme === 'dark'
  const T = dark ? {
    box: { background: '#0f172a', border: '1px solid #1e3a5f' },
    head: { background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)' },
    lead: '#94a3b8', title: '#e2e8f0', desc: '#64748b',
    price: '#e2e8f0', priceSub: '#64748b', planBorder: '#1e3a5f', foot: '#475569',
  } : {
    box: { background: '#fff', border: '2px solid #3B82F6' },
    head: { background: '#2563eb' },
    lead: '#475569', title: '#1e293b', desc: '#64748b',
    price: '#1e293b', priceSub: '#64748b', planBorder: '#e2e8f0', foot: '#94a3b8',
  }

  return (
    <div className="rounded-2xl overflow-hidden" style={T.box}>
      <div className="px-4 py-2 text-center" style={T.head}>
        <span className="text-white text-xs font-bold tracking-wide">{t.banner}</span>
      </div>
      <div className="p-5">
        {!compact && (
          <p className="text-sm mb-4" style={{ color: T.lead }}>
            {t.lead[0]}<strong>{t.lead[1]}</strong>{t.lead[2]}
          </p>
        )}
        <ul className="space-y-2 mb-5">
          {t.features.map(([ic, head, d]) => (
            <li key={head} className="flex gap-2.5">
              <span className="text-lg leading-none mt-0.5">{ic}</span>
              <div>
                <div className="text-sm font-medium" style={{ color: T.title }}>{head}</div>
                <div className="text-xs mt-0.5" style={{ color: T.desc }}>{d}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {(plans || FALLBACK).map(p => (
            <div key={p.id} className="rounded-xl px-3 py-3 text-center"
              style={{ border: `1px solid ${p.highlight ? '#3B82F6' : T.planBorder}` }}>
              <div className="text-[11px] font-medium mb-1" style={{ color: T.desc }}>{t.plan[p.id]}</div>
              <div className="flex items-baseline justify-center gap-0.5">
                <span className="text-xl font-black tabular-nums" style={{ color: T.price }}>{p.price}</span>
                <span className="text-[11px]" style={{ color: T.priceSub }}>{t.unit}</span>
              </div>
              <div className="text-[11px] mt-1" style={{ color: T.desc }}>{t.tracked(p.tracked)}</div>
              {p.stale && (
                <div className="text-[10px] mt-1 opacity-70" style={{ color: T.desc }}>{t.stale}</div>
              )}
            </div>
          ))}
        </div>
        <a href={paidUrl || '#'} target="_blank" rel="noopener noreferrer"
          className="block w-full py-3 rounded-xl text-white text-sm font-bold text-center"
          style={{ background: dark ? 'linear-gradient(135deg,#3B82F6,#8B5CF6)' : '#2563eb' }}>
          {t.cta}
        </a>
        <p className="text-[11px] text-center mt-2" style={{ color: T.foot }}>{t.foot}</p>
      </div>
    </div>
  )
}
